import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useModels } from "@/components/chat/Composer";
import { useState, type FormEvent } from "react";
import { Route, Routes } from "react-router-dom";
import McpServers from "@/components/McpServers";
import PageShell from "@/components/PageShell";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Select, Textarea } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe, useSetMe } from "@/lib/auth";
import { APP_NAME } from "@/lib/brand";
import { getTheme, setTheme, type Theme } from "@/lib/theme";
import { disableNotify, enableNotify, notifyEnabled, notifySupport, showNotification } from "@/lib/notify";
import { useVoiceStatus } from "@/lib/voice/controller";
import type { SessionInfo, User } from "@/lib/types";
import Archived from "./settings/Archived";
import CompanionSettings from "./settings/CompanionSettings";
import MemorySettings from "./settings/MemorySettings";
import NotificationSettings from "./settings/NotificationSettings";
import TemplateSettings from "./settings/TemplateSettings";
import WorkerSettings from "./settings/WorkerSettings";

const tabs = [
  { to: "/settings", label: "General" },
  { to: "/settings/memory", label: "Memory" },
  { to: "/settings/templates", label: "Templates" },
  { to: "/settings/notifications", label: "Notifications" },
  { to: "/settings/mcp", label: "MCP servers" },
  { to: "/settings/workers", label: "Workers" },
  { to: "/settings/companion", label: "Desktop app" },
  { to: "/settings/archived", label: "Archived chats" },
];

export default function Settings() {
  return (
    <PageShell title="Settings" tabs={tabs}>
      <Routes>
        <Route index element={<General />} />
        <Route path="memory" element={<MemorySettings />} />
        <Route path="templates" element={<TemplateSettings />} />
        <Route path="notifications" element={<NotificationSettings />} />
        <Route path="mcp" element={<McpServers admin={false} />} />
        <Route path="workers" element={<WorkerSettings />} />
        <Route path="companion" element={<CompanionSettings />} />
        <Route path="archived" element={<Archived />} />
      </Routes>
    </PageShell>
  );
}

function General() {
  return (
    <>
      <Profile />
      <Personalization />
      <DefaultModel />
      <Appearance />
      <ReplyNotifications />
      <VoicePick />
      <Password />
      <Sessions />
      <ApiTokens />
    </>
  );
}

function Profile() {
  const me = useMe().data!;
  const setMe = useSetMe();
  const [name, setName] = useState(me.name);
  const save = useMutation({
    mutationFn: () => api.patch<User>("/api/auth/me", { name }),
    onSuccess: setMe,
  });
  return (
    <Card title="Profile">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Username">
            <Input value={me.username} disabled />
          </Label>
          <Label label="Display name">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Label>
        </div>
        <ErrorText error={save.error} />
        <Button disabled={save.isPending || name === me.name}>Save</Button>
      </form>
    </Card>
  );
}

function Personalization() {
  const me = useMe().data!;
  const setMe = useSetMe();
  const [prompt, setPrompt] = useState(String(me.settings.system_prompt ?? ""));
  const save = useMutation({
    mutationFn: () => api.patch<User>("/api/auth/me", { settings: { system_prompt: prompt } }),
    onSuccess: setMe,
  });
  return (
    <Card title="Personalization">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Label label="System prompt" hint="Sent at the start of every chat. Tell the assistant about yourself or how it should answer.">
          <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        </Label>
        <Checkbox
          label="Let vision models see images that tools create (e.g. image generation), so they can check and describe them"
          checked={me.settings.tool_images !== false}
          onChange={(e) => api.patch<User>("/api/auth/me", { settings: { tool_images: e.target.checked } }).then(setMe)}
        />
        <ErrorText error={save.error} />
        <Button disabled={save.isPending || prompt === String(me.settings.system_prompt ?? "")}>Save</Button>
      </form>
    </Card>
  );
}

function VoicePick() {
  const me = useMe().data!;
  const setMe = useSetMe();
  const status = useVoiceStatus().data;
  const save = useMutation({ mutationFn: (v: string) => api.patch<User>("/api/auth/me", { settings: { voice: v } }), onSuccess: setMe });
  if (!status?.tts || status.voices.length < 2) return null;
  return (
    <Card title="Voice">
      <Label label="Voice for spoken replies">
        <Select className="max-w-xs" value={String(me.settings.voice ?? "")} onChange={(e) => save.mutate(e.target.value)}>
          <option value="">Default ({status.default_voice})</option>
          {status.voices.map((v) => (
            <option key={v}>{v}</option>
          ))}
        </Select>
      </Label>
    </Card>
  );
}

function DefaultModel() {
  const me = useMe().data!;
  const setMe = useSetMe();
  const qc = useQueryClient();
  const models = useModels().data ?? [];
  const save = useMutation({
    mutationFn: (id: string) => api.patch<User>("/api/auth/me", { settings: { default_model_id: id || null } }),
    onSuccess: (u) => {
      setMe(u);
      qc.invalidateQueries({ queryKey: ["models"] });
    },
  });
  const site = models.find((m) => m.site_default);
  return (
    <Card title="Default model">
      <Label
        label="Model for new chats"
        hint={`Without one, new chats use the model you used last in this browser${site ? `, or ${site.name} (the site default)` : ""}.`}
      >
        <Select className="max-w-sm" value={String(me.settings.default_model_id ?? "")} onChange={(e) => save.mutate(e.target.value)}>
          <option value="">The one I used last</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({m.endpoint})
            </option>
          ))}
        </Select>
      </Label>
      <ErrorText error={save.error} />
    </Card>
  );
}

function ReplyNotifications() {
  const [on, setOn] = useState(notifyEnabled());
  const [note, setNote] = useState<string | null>(null);
  const support = notifySupport();
  async function toggle(want: boolean) {
    setNote(null);
    if (!want) {
      disableNotify();
      setOn(false);
      return;
    }
    const permission = await enableNotify();
    setOn(permission === "granted");
    if (permission === "denied") setNote("The browser blocks notifications for this site. Allow them in the site settings (the icon next to the address).");
  }
  return (
    <Card title="Notifications">
      <div className="space-y-3">
        <Checkbox
          label="Notify me when a reply finishes while this tab is in the background"
          checked={on}
          disabled={support !== "ok"}
          onChange={(e) => toggle(e.target.checked)}
        />
        <p className="text-xs text-zinc-500">
          {support === "insecure"
            ? "Browsers only allow notifications on https (or localhost). Open the app through its https address to use this."
            : support === "unsupported"
              ? "This browser does not support notifications. On iPhone, add the app to the home screen first (Share → Add to Home Screen)."
              : "Set per device and browser."}
        </p>
        {note && <p className="text-sm text-amber-700 dark:text-amber-400">{note}</p>}
        {on && (
          <Button size="sm" variant="secondary" onClick={() => showNotification(APP_NAME, "Notifications work on this device.", "/settings", "test", true)}>
            Send a test notification
          </Button>
        )}
      </div>
    </Card>
  );
}

function Appearance() {
  const [theme, set] = useState<Theme>(getTheme());
  return (
    <Card title="Appearance">
      <Label label="Theme">
        <Select
          value={theme}
          onChange={(e) => {
            const t = e.target.value as Theme;
            set(t);
            setTheme(t);
          }}
          className="max-w-xs"
        >
          <option value="system">System</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </Select>
      </Label>
    </Card>
  );
}

function Password() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: () => api.post("/api/auth/password", form),
    onSuccess: () => {
      setForm({ current_password: "", new_password: "" });
      setDone(true);
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    change.mutate();
  }
  return (
    <Card title="Password">
      <form className="space-y-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Current password">
            <Input type="password" autoComplete="current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
          </Label>
          <Label label="New password">
            <Input type="password" autoComplete="new-password" minLength={8} required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
          </Label>
        </div>
        <ErrorText error={change.error} />
        {done && <p className="text-sm text-green-600">Password changed.</p>}
        <Button disabled={change.isPending}>Change password</Button>
      </form>
    </Card>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api.get<SessionInfo[]>("/api/auth/sessions") });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  return (
    <Card title="Signed-in devices">
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {sessions.data?.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-4 py-2.5">
            <div className="min-w-0">
              <p className="truncate text-sm">{describeAgent(s.user_agent)}</p>
              <p className="text-xs text-zinc-500">Last active {new Date(s.last_seen_at).toLocaleString()}</p>
            </div>
            {s.current ? (
              <Badge tone="green">This device</Badge>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => revoke.mutate(s.id)}>
                Sign out
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface ApiToken {
  id: string;
  name: string;
  admin: boolean;
  created_at: string;
  last_seen_at: string;
}

function ApiTokens() {
  const qc = useQueryClient();
  const tokens = useQuery({ queryKey: ["api-tokens"], queryFn: () => api.get<ApiToken[]>("/api/auth/tokens") });
  const [name, setName] = useState("");
  const [admin, setAdmin] = useState(false);
  const isAdmin = useMe().data?.role === "admin";
  const [shown, setShown] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<ApiToken & { token: string }>("/api/auth/tokens", { name: name.trim(), admin: isAdmin && admin }),
    onSuccess: (t) => {
      setShown(t.token);
      setName("");
      setAdmin(false);
      qc.invalidateQueries({ queryKey: ["api-tokens"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
  });
  return (
    <Card title="API tokens">
      <p className="mb-3 text-sm text-zinc-500">
        For apps that are not a browser, such as the desktop companion. A token acts as you for chats and models, but cannot
        manage your account. It is shown once.
      </p>
      {shown && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
          <p className="mb-1">Copy this token now; it will not be shown again.</p>
          <code className="block break-all select-all">{shown}</code>
        </div>
      )}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {tokens.data?.map((t) => (
          <li key={t.id} className="flex items-center justify-between gap-4 py-2.5">
            <div className="min-w-0">
              <p className="truncate text-sm">
                {t.name} {t.admin && <Badge tone="red">Admin</Badge>}
              </p>
              <p className="text-xs text-zinc-500">Last used {new Date(t.last_seen_at).toLocaleString()}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => revoke.mutate(t.id)}>
              Revoke
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <Input className="max-w-xs" placeholder="Name, e.g. Desktop companion" value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          Create token
        </Button>
      </form>
      {isAdmin && (
        <div className="mt-2">
          <Checkbox label="Admin access" checked={admin} onChange={(e) => setAdmin(e.target.checked)} />
          <p className="ml-6 text-xs text-zinc-500">
            Lets the token use the admin API (users, endpoints, MCP servers). Anyone who gets it can act as an admin until you revoke it; only tick this if the app needs it.
          </p>
        </div>
      )}
      <ErrorText error={create.error} />
    </Card>
  );
}

function describeAgent(ua: string): string {
  if (!ua) return "Unknown device";
  const browser = /Firefox\//.test(ua) ? "Firefox" : /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}
